import React, { useState, useEffect } from 'react';
import { ErrorMessage, Formik } from 'formik';
import Recaptcha from 'react-recaptcha';

import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { RecaptchaSiteKey } from '../Data/recaptcha';

import SelectDonationType from './SelectDonationType';
import SelectAmountToDonateSimple from './SelectAmountToDonateSimple';
import DonatePersonalInformation from './DonatePersonalInformation';
import simpleDonationSchema from '../Schemas/simpleDonation-schema';
import SimpleDonationCard from './SimpleDonationCard';
import DonatePaymentDetails from './DonatePaymentDetails';

const SimpleDonationForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [uniqueId] = useState(Math.random().toString(36).substr(2, 9));

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  return (
    <Formik
      initialValues={{
        donationType: 'oneOff',
        donationAmount: '50',
        otherAmount: '',
        fullName: '',
        email: '',
        address: '',
        phone: { country: 'NZ', number: '' },
        paymentMethod: 'card',
        showRecaptcha: window.showRecaptcha != 'NZ' && window.showRecaptcha != 'AUS',
        recaptcha: null,
      }}
      validationSchema={simpleDonationSchema}
      // if we validate on blur or change we will have to think about phone validation otherwise it will trigger lots of ajax requests
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={async (values, actions) => {
        // recaptcha server side validation
        if (window.showRecaptcha != 'NZ' && window.showRecaptcha != 'AUS') {
          await window
            .fetch('/recaptcha/validate/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                recaptcha: values.recaptcha,
              }),
            })
            .then((response) => response.json())
            .then(async (result) => {
              if (!result.success) {
                values.recaptcha = null;
                await grecaptcha.reset();
                await actions.validateField('recaptcha');
                return false;
              }
            });
        }

        await setLoading(true);
        if (values.paymentMethod == 'card') {
          if (!stripe || !elements) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            setLoading(false);
            return;
          }

          // Get a reference to a mounted CardElement. Elements knows how
          // to find your CardElement because there can only ever be one of
          // each type of element.
          const cardElement = elements.getElement(CardElement);

          // Use your card Element with other Stripe.js APIs
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
          });

          // Check if card is valid, like a basic validations
          if (!error) {
            // generate entry using email and store in DB
            await window.fetch('/stripe/log-donation/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                identifier: uniqueId,
                formData: values,
              }),
            });

            if (values.donationType == 'oneOff') {
              // create intent
              await window
                .fetch('/stripe/create-payment-intent/', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    metadata: {
                      id: 'simple-donate',
                      donationAmount: values.donationAmount,
                      otherAmount: values.otherAmount,
                      fullName: values.fullName,
                      email: values.email,
                      address: values.address,
                      phone: values.phone,
                    },
                  }),
                })
                .then((res) => {
                  return res.json();
                })
                .then((data) => {
                  window
                    .fetch('/stripe/log-donation/', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        identifier: uniqueId,
                        donationType: 'Card',
                        donationCampaign: 'Simple donation',
                        paymentIntent: data.clientSecret,
                        donationFrequency: values.donationType,
                        donationAmount: values.donationAmount,
                        otherAmount: values.otherAmount,
                        fullName: values.fullName,
                        email: values.email,
                        address: values.address,
                        phone: values.phone,
                      }),
                    })
                    .catch((error) => {
                      window
                        .fetch('/stripe/log-donation/', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            identifier: uniqueId,
                            donationType: 'Card',
                            donationCampaign: 'Simple donation',
                            paymentIntent: data.clientSecret,
                            donationFrequency: values.donationType,
                            donationAmount: values.donationAmount,
                            otherAmount: values.otherAmount,
                            fullName: values.fullName,
                            email: values.email,
                            address: values.address,
                            phone: values.phone,
                          }),
                        })
                        .then((response) => response.json())
                        .then(async (donationId) => {
                          await window.fetch('/stripe/log-donation/', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              identifier: uniqueId,
                                donationStatus: "successful",
                            }),
                          });
                          window.location.replace(
                            `/our-supporters/thank-you/?id=${result.paymentIntent.id}&code=card&internal=${donationId}`
                          );
                        })
                        .catch(async (error) => {
                          await window.fetch('/stripe/log-donation/', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              identifier: uniqueId,
                              donationStatus: "failed",
                            }),
                          });
                          window.location.replace(
                            `/our-supporters/declined/?id=${result.paymentIntent.id}&code=card&internal=${donationId}`
                          );
                        });
                    });

                  stripe
                    .confirmCardPayment(data.clientSecret, {
                      payment_method: {
                        card: elements.getElement(CardElement),
                      },
                    })
                    .then(function (result) {
                      window
                        .fetch('/stripe/log-donation/', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            identifier: uniqueId,
                            content: result,
                          }),
                        })
                        .then((response) => response.json())
                        .then(async (donationId) => {
                          setLoading(false);
                          if (result.error) {
                              await window.fetch('/stripe/log-donation/', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  identifier: uniqueId,
                                  donationStatus: "failed",
                                }),
                              });
                            window.location.replace(
                              `/our-supporters/declined/?id=${result.error.payment_intent.id}&code=card&internal=${donationId}`
                            );
                          } else {
                            await window.fetch('/stripe/log-donation/', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({
                                identifier: uniqueId,
                                donationStatus: "successful",
                              }),
                            });
                            window.location.replace(
                              `/our-supporters/thank-you/?id=${result.paymentIntent.id}&code=card&internal=${donationId}`
                            );
                          }
                        })
                        .catch(async (error) => {
                          await window.fetch('/stripe/log-donation/', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              identifier: uniqueId,
                              donationStatus: "failed",
                            }),
                          });
                          window.location.replace(
                            `/our-supporters/declined/?id=${result.paymentIntent.id}&code=card&internal=${donationId}`
                          );
                        });
                    });
                });
            } else {
              // create subscription
              window
                .fetch('/stripe/create-payment-subscription/', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    identifier: uniqueId,
                    paymentMethodId: paymentMethod.id,
                    metadata: {
                      id: 'simple-donate-monthly',
                      donationAmount: values.donationAmount,
                      otherAmount: values.otherAmount,
                      fullName: values.fullName,
                      email: values.email,
                      address: values.address,
                      phone: values.phone,
                    },
                  }),
                })
                .then((res) => {
                  return res.json();
                })
                .then((subscription) => {
                  window
                    .fetch('/stripe/log-donation/', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        identifier: uniqueId,
                        donationType: 'Card',
                        donationCampaign: 'Simple donation',
                        content: subscription,
                        donationFrequency: values.donationType,
                        donationAmount: values.donationAmount,
                        otherAmount: values.otherAmount,
                        fullName: values.fullName,
                        email: values.email,
                        address: values.address,
                        phone: values.phone,
                      }),
                    })
                    .then((response) => response.json())
                    .then(async (result) => {
                      setLoading(false);
                      if (subscription.status === 'active') {
                        await window.fetch('/stripe/log-donation/', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            identifier: uniqueId,
                            donationStatus: "successful",
                          }),
                        });
                        window.location.replace(
                          `/our-supporters/thank-you/?id=${subscription.plan.id}&code=card-monthly&internal=${result}`
                        );
                      } else {
                        await window.fetch('/stripe/log-donation/', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            identifier: uniqueId,
                            donationStatus: "failed",
                          }),
                        });
                        window.location.replace(
                          `/our-supporters/declined/?id=${subscription.plan.id}&code=card-monthly&internal=${result}`
                        );
                      }
                    })
                    .catch(async (error) => {
                      await window.fetch('/stripe/log-donation/', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          identifier: uniqueId,
                          donationStatus: "failed",
                        }),
                      });
                      window.location.replace(
                        `/our-supporters/declined/?id=${subscription.plan.id}&code=card-monthly&internal=${result}`
                      );
                    });
                });
            }
          } else {
            if (error.type != 'validation_error') {
              window
                .fetch('/stripe/log-donation/', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    identifier: uniqueId,
                    donationType: 'Card',
                    donationCampaign: 'Simple donation',
                    paymentIntent: error.message,
                    donationFrequency: values.donationType,
                    donationAmount: values.donationAmount,
                    otherAmount: values.otherAmount,
                    fullName: values.fullName,
                    email: values.email,
                    address: values.address,
                    phone: values.phone,
                  }),
                })
                .then((response) => response.json())
                .then(async (donationId) => {
                  await window.fetch('/stripe/log-donation/', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      identifier: uniqueId,
                      donationStatus: "failed",
                    }),
                  });
                  window.location.replace(`/our-supporters/declined/?code=card&internal=${donationId}`);
                })
                .catch(async (error) => {
                  await window.fetch('/stripe/log-donation/', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      identifier: uniqueId,
                      donationStatus: "failed",
                    }),
                  });
                  window.location.replace(`/our-supporters/declined/?code=card&internal=${donationId}`);
                });
            }
            setLoading(false);
          }
        } else {
          window
            .fetch('/stripe/log-donation/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                identifier: uniqueId,
                formData: values,
                donationType: 'Direct debit',
                donationCampaign: 'Simple donation',
                donationFrequency: values.donationType,
                donationAmount: values.donationAmount,
                otherAmount: values.otherAmount,
                fullName: values.fullName,
                email: values.email,
                address: values.address,
                phone: values.phone,
              }),
            })
            .then((response) => response.json())
            .then(async (result) => {
              setLoading(false);
              await window.fetch('/stripe/log-donation/', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  identifier: uniqueId,
                  donationStatus: "successful",
                }),
              });
              window.location.replace(`/our-supporters/thank-you/?code=direct-debit&internal=${result}`);
            })
            .catch(async (error) => {
              await window.fetch('/stripe/log-donation/', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  identifier: uniqueId,
                  donationStatus: "failed",
                }),
              });
              window.location.replace(`/our-supporters/declined/?code=direct-debit&internal=${result}`);
            });
        }
      }}
    >
      {(props) => {
        return (
          <form
            id="form-donate"
            // action="#footnote"
            method="post"
            className="form-new form-donate"
            noValidate
            onSubmit={props.handleSubmit}
          >
            <div className="section-inner">
              <SelectDonationType
                index="1"
                heading="select a donation type"
                label1="One-off donation"
                label2="Give monthly"
                extraUlClass="horizontal"
                {...props}
              />
              <SelectAmountToDonateSimple {...props} />
              <DonatePersonalInformation index="2" heading="personal information" {...props} />

              {/* <div className="form-step"> */}
              {/*  <div className="membership-total-cost"> */}
              {/*    Total: ${props.values.donationAmount} <span>/ Year</span> */}
              {/*  </div> */}
              {/* </div> */}

              <DonatePaymentDetails index="3" heading="payment details" {...props} />

              {props.values.showRecaptcha && (
                <div className="recaptcha-field">
                  <Recaptcha
                    sitekey={RecaptchaSiteKey}
                    render="explicit"
                    theme="light"
                    verifyCallback={(response) => {
                      props.setFieldValue('recaptcha', response);
                    }}
                    onloadCallback={() => {
                      // console.log('done loading!');
                    }}
                  />
                  <ErrorMessage name="recaptcha">
                    {(msg) => (
                      <ul className="validationErrors">
                        <li id="recaptcha-error" className="formValidationError">
                          {msg}
                        </li>
                      </ul>
                    )}
                  </ErrorMessage>
                </div>
              )}

              <button
                id="js-submit-button"
                disabled={!stripe || props.isSubmitting || loading}
                type="submit"
                className="button readmore"
              >
                {props.isSubmitting || loading ? 'Loading...' : 'Pay now'}
              </button>
            </div>
            <div className="footnote-new">
              <span >Auckland Writers Festival is a registered charity:<br />
              </span><span ></span>Auckland Writers &amp; Readers Festival Charitable Trust - Reg CC25635. All donations over $5 are tax deductible and will be acknowledged with a receipt by email.
	          </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default SimpleDonationForm;
